import { PhraseType } from "../api/phrasebook";
import {
  BrandBatchSettings,
  ContentGroup,
  ContentGroupKind,
  MAX_CONTENT_GROUPS,
  defaultContentGroupFormatting,
} from "../pages/playground/types";
import { normalizeFieldName } from "./fieldNames";

// ── Mapping: PhraseType enum → slot metadata ───────────────────────

interface SlotInfo {
  kind: ContentGroupKind;
  label: string;
}

/** Maps PhraseType enum values to their kind and default label */
export const PHRASE_TYPE_SLOTS: Record<string, SlotInfo> = {
  [PhraseType.TITLE]: { kind: "title", label: "Title" },
  [PhraseType.SHORT_DESCRIPTION]: { kind: "description", label: "Short Description" },
  [PhraseType.EDITORIAL_DESCRIPTION]: { kind: "description", label: "Regular Description" },
  [PhraseType.LONG_DESCRIPTION]: { kind: "description", label: "Long Description" },
  [PhraseType.BULLET_POINTS]: { kind: "bullet_points", label: "Bullet Points" },
  [PhraseType.BULLET_POINTS_1]: { kind: "bullet_points", label: "Bullet Points Extra 1" },
  [PhraseType.BULLET_POINTS_2]: { kind: "bullet_points", label: "Bullet Points Extra 2" },
  // Dynamic description slots (4-15)
  ...Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => {
      const n = i + 4;
      return [`DESCRIPTION_${n}`, { kind: "description" as ContentGroupKind, label: `Description ${n}` }];
    })
  ),
  // Dynamic bullet point slots (3-14)
  ...Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => {
      const n = i + 3;
      return [`BULLET_POINTS_${n}`, { kind: "bullet_points" as ContentGroupKind, label: `Bullet Points ${n}` }];
    })
  ),
};

/** Available slots for new description groups */
export const AVAILABLE_DESCRIPTION_SLOTS = Array.from(
  { length: 12 },
  (_, i) => `DESCRIPTION_${i + 4}` as PhraseType
);

/** Available slots for new bullet point groups */
export const AVAILABLE_BULLET_SLOTS = Array.from(
  { length: 12 },
  (_, i) => `BULLET_POINTS_${i + 3}` as PhraseType
);

// ── Helpers ───────────────────────────────────────────────────────────

const DEFAULT_PHRASES = {
  minPhrases: 1,
  maxPhrases: 1,
  minCharacters: 0,
  maxCharacters: 0,
  minWords: 0,
  maxWords: 0,
};

/**
 * Reads content_groups from settings.
 * After migration, all settings have content_groups.
 */
export function getContentGroups(
  settings: BrandBatchSettings | undefined
): ContentGroup[] {
  return settings?.content_groups || [];
}

/** Returns only enabled groups, sorted by sortOrder. */
export function getEnabledGroups(
  settings: BrandBatchSettings | undefined
): ContentGroup[] {
  return getContentGroups(settings)
    .filter((g) => g.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Finds a group by its PhraseType id. */
export function getGroupById(
  settings: BrandBatchSettings | undefined,
  id: string
): ContentGroup | undefined {
  return getContentGroups(settings).find((g) => g.id === id);
}

/** Gets the next available slot for a given kind, or null if none. */
export function getNextAvailableSlot(
  groups: ContentGroup[],
  kind: "description" | "bullet_points"
): PhraseType | null {
  const usedIds = new Set(groups.map((g) => g.id));
  const slots =
    kind === "description" ? AVAILABLE_DESCRIPTION_SLOTS : AVAILABLE_BULLET_SLOTS;

  // Also check legacy slots that might be unused
  const legacySlots =
    kind === "description"
      ? [
          PhraseType.SHORT_DESCRIPTION,
          PhraseType.EDITORIAL_DESCRIPTION,
          PhraseType.LONG_DESCRIPTION,
        ]
      : [PhraseType.BULLET_POINTS_1, PhraseType.BULLET_POINTS_2];

  for (const slot of [...legacySlots, ...slots]) {
    if (!usedIds.has(slot)) return slot;
  }
  return null;
}

/** Creates a new ContentGroup with defaults for the given kind. */
export function createContentGroup(
  id: string,
  kind: "description" | "bullet_points",
  sortOrder: number
): ContentGroup {
  const slot = PHRASE_TYPE_SLOTS[id];
  return {
    id,
    kind,
    label: slot?.label || (kind === "description" ? "New Description" : "New Bullet Points"),
    enabled: true,
    sortOrder,
    phrases: { ...DEFAULT_PHRASES },
    formatting:
      kind === "bullet_points"
        ? { ...defaultContentGroupFormatting, phraseSeparator: "newline" }
        : { ...defaultContentGroupFormatting },
  };
}

/**
 * Switches the kind of a group (description ↔ bullet_points) in place.
 * Reassigns the id to the next available slot of the new kind, preserves the
 * phrase limits and the custom label (if the user had edited it), and resets
 * formatting defaults for the new kind.
 *
 * Returns the updated groups array, or null if the operation is not possible
 * (group not found, group is title, no available slot of the target kind).
 */
export function switchGroupKind(
  groups: ContentGroup[],
  groupId: string
): ContentGroup[] | null {
  const current = groups.find((g) => g.id === groupId);
  if (!current || current.kind === "title") return null;

  const newKind: "description" | "bullet_points" =
    current.kind === "description" ? "bullet_points" : "description";

  // Pass all groups except the current one so its own slot is available
  const otherGroups = groups.filter((g) => g.id !== groupId);
  const newSlot = getNextAvailableSlot(otherGroups, newKind);
  if (!newSlot) return null;

  // Keep the custom label if the user had edited it (i.e. it differs from the
  // default of the current slot). Otherwise use the new slot's default label.
  const currentDefaultLabel = PHRASE_TYPE_SLOTS[current.id]?.label;
  const labelWasCustomized =
    !!currentDefaultLabel && current.label !== currentDefaultLabel;
  const newDefaultLabel = PHRASE_TYPE_SLOTS[newSlot]?.label || current.label;
  const label = labelWasCustomized ? current.label : newDefaultLabel;

  const newFormatting =
    newKind === "bullet_points"
      ? { ...defaultContentGroupFormatting, phraseSeparator: "newline" }
      : { ...defaultContentGroupFormatting };

  const updated: ContentGroup = {
    id: newSlot,
    kind: newKind,
    label,
    enabled: current.enabled,
    sortOrder: current.sortOrder,
    phrases: { ...current.phrases },
    formatting: newFormatting,
  };

  return groups.map((g) => (g.id === groupId ? updated : g));
}

/** Checks whether the max group limit has been reached. */
export function canAddGroup(groups: ContentGroup[]): boolean {
  return groups.length < MAX_CONTENT_GROUPS;
}

/** Converts a ContentGroup to the flat BrandBatchSettingsWithPhrases shape used by validation. */
export function toPhraseLimits(group: ContentGroup) {
  return { enabled: group.enabled, ...group.phrases };
}

/** Gets the export column name for a group. */
export function getGroupExportName(group: ContentGroup): string {
  return normalizeFieldName(group.label);
}

/**
 * Gets the label for a PhraseType from content_groups, falling back to slot defaults.
 */
export function getGroupLabelByPhraseType(
  settings: BrandBatchSettings | undefined,
  phraseType: PhraseType | string
): string {
  const group = getGroupById(settings, phraseType);
  if (group) return group.label;
  const slot = PHRASE_TYPE_SLOTS[phraseType];
  return slot?.label || "Unknown";
}
